import React, {useEffect, useState, useRef, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Tab, TabList, TabPanel, Tabs} from 'react-tabs';
import _merge from 'lodash/merge';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
import moment from 'moment';

import CalculationCouponForm from './CalculationCouponForm/CalculationCouponForm';
import PuritiesForm from './PuritiesForm/PuritiesForm';
import ThingsForm from './ThingsForm/ThingsForm';
import CarsForm from './CarsForm/CarsForm';
import CalculationLayout from './CalculationTabs/CalculationLayout/CalculationLayout';
import {
    requestForCalculation, resetCalculations,
} from '../../../redux/actions/quickCalculationAction';
import {
    contractTypeJewellery, contractTypeThings, contractTypeCars,
} from '../../../redux/actions/contractAction';
import {
    QUICK_CALCULATION_CHECKSUM, QUICK_CALCULATION_DATA,
} from '../../../constants/localStorageConstants';
import {getCashbox} from '../../../redux/actions/handbookAction';
import {authService} from '../../../services/auth-service';
import {calculationAccess, permissionsAccess} from '../../../helpers/common';
import { dealTypes, listRoles} from '../../../helpers/data-constants';
import {getAvailableCashboxes} from '../../../redux/actions/dashboardAction';
import _debounce from "lodash/debounce";
import useContractForm from './useContractHook';
import './contactForm.scss';

const defaultPurities = [{
    jewel: {value: '', label: ''},
    purity: {label: '', value: ''},
    quantity: 1,
    grossWeight: 0,
    pureWeight: 0,
    puritiesComment: '',
    showComment: false,
    isPart: false,
    additionalInfo: '',
},];

const defaultThings = {
    name: '', model: '', quantity: 1, imei: '', sn: '', price: 0, estimatedValue: '', additionalInfo: ''
};

const defaultCars = {
    makeOfCar: {label: '', value: ''},
    model: {label: '', value: ''},
    year: {label: '', value: ''},
    carNumber: '',
    quantity: 1,
    marketPrice: '',
    estimatedPrice: '',
    registrationNumber: '',
    registrationDate: '',
    VIN: '',
    condition: '',
    additionalInfo: '',
    garage: null,
};

function ContractForm({
                          loadSavedData = false,
                          isClear,
                          onError,
                          setContextForm,
                          setContextCalculationLayout,
                          clientId,
                          contextCalculationLayout,
                          isDebtLoadData,
                          handleCloseDebtLoadModal,
                          currentDealDuration
                      }) {
    const dispatch = useDispatch();
    const {cashbox} = useSelector(({handbook}) => handbook);
    const {calculations, commonError, wasSaved, calculationFaild} = useSelector((state) => state.quickCalculation);
    const {accessFeatures} = useSelector(({user}) => user);
    const {cashboxes} = useSelector(({dashboard}) => dashboard);
    const [watchEstimated, setWatchEstimated] = useState(defaultThings.estimatedValue)
    const [formState, setFormState] = useState(() => {
        const newFormState = {
            typeTab: 0,
            coupon: {},
            cashbox: {},
            maxDuration: null,
            minDuration: null,
            calculation: {},
            purities: _cloneDeep(defaultPurities),
            things: {...defaultThings},
            cars: _cloneDeep(defaultCars),
            additionalInformation: ''
        };
        if (loadSavedData && wasSaved) {
            _merge(newFormState, JSON.parse(localStorage.getItem(QUICK_CALCULATION_DATA)));
        } else {
            dispatch(resetCalculations());
        }
        return newFormState;
    });
    const [waiverOfRedemption, setWaiverOfRedemption] = useState(!!formState.calculation.waiverOfRedemption)
    const [isCalculationFromAmount, setIsCalculationFromAmount] = useState(false);
    const [isEstimatedValue, setIsEstimatedValue] = useState(false);
    const [renderCalculationCouponForm, setRenderCalculationCouponForm] = useState(false);
    const emptyReq = useRef({});
    const adminRole = calculationAccess([listRoles.ADMIN])
    const isAccessWaiverOfRedemption = permissionsAccess('deal.waiver_of_redemption')
    const shouldRenderWaiverOfRedemption =
        (isAccessWaiverOfRedemption && !accessFeatures['visible.waiver.of.redemption']) ||
        accessFeatures['visible.waiver.of.redemption'];
    const isEstimatedEqualsAmount = accessFeatures['things.calculation.estimated.equals.amount'] && isEstimatedValue
    const isAnnuityFeature = accessFeatures['deal.annuity']

    const {
        dealFormTabs,
        activeDealTypeIndex,
        showAnnuityAuto,
        handleTypeTabSelect,
        handleDealTypeTabSelect,
      } = useContractForm({formState, cashbox, defaultPurities, defaultThings, defaultCars, setFormState})

    useEffect(() => {
        dispatch(contractTypeJewellery())
    }, [dispatch])

    useEffect(() => {
        if (adminRole) {
            dispatch(getAvailableCashboxes());

        } else {
            const activeCashBoxId = authService.getActiveCashBoxId().value;
            dispatch(getCashbox({id: activeCashBoxId, type: dealTypes[formState.dealForm]?.couponType}));
        }
    }, [dispatch]);

    useEffect(() => {
        if (wasSaved) {
            switch (formState.typeTab) {
                case 0:
                    dispatch(contractTypeJewellery())
                    break;
                case 1:
                    dispatch(contractTypeThings())
                    break;
                case 2 :
                    dispatch(contractTypeCars())
                    break;
                default:
                    return
            }
        }
    }, [dispatch, formState.typeTab, wasSaved])

    useEffect(() => {
        if (!calculations.totalEstimatedValue) return;

        if (formState.typeTab === 1) {
            if (isCalculationFromAmount) {
                setFormState((prev) => ({
                    ...prev, things: {
                        ...prev.things, estimatedValue: calculations.totalEstimatedValue.toFixed(2),
                    },
                }));
                setIsCalculationFromAmount(true);
            }
        }
        if (formState.typeTab === 2 && isCalculationFromAmount) {
            setFormState((prev) => ({
                ...prev, cars: {
                    ...prev.cars, estimatedPrice: calculations.totalEstimatedValue.toFixed(2),
                },
            }));
            setIsCalculationFromAmount(false);
        }
    }, [calculations.totalEstimatedValue, formState.typeTab, isCalculationFromAmount, isEstimatedValue]);

    useEffect(() => {
        localStorage.setItem(QUICK_CALCULATION_CHECKSUM, calculations.checksum);
    }, [calculations]);

    useEffect(() => {

        let type;
        switch (formState.typeTab) {
            case 0:
                type = 'PURITY';
                break;
            case 1:
                type = 'THINGS'
                break;
            case 2 :
                type = 'CARS'
                break;
            default:
                return null
        }

        const recalculate = _debounce((req) => {
            const isEqualToEmptyReq = _isEqual(emptyReq.current, req);
            const hasCommonFields = req.couponId && req.amount && (req.date || req.months);
            const hasCalculationFeatures = accessFeatures['calculationAmountContractsThings'];

            const dispatchCalculation = () => {
                emptyReq.current = {...req};
                dispatch(requestForCalculation(req));
            };

            if (req.type === 'THINGS' || req.type === 'CARS') {
                if (hasCommonFields && !isEqualToEmptyReq && !req.estimatedValue) {
                    setIsCalculationFromAmount(true)
                    dispatchCalculation();
                } else if (req.couponId && req.estimatedValue && !isEqualToEmptyReq && +req.estimatedValue >= 0) {
                    if(hasCommonFields) {
                        setIsEstimatedValue(true)
                        dispatchCalculation();
                    }
                }
            } else {
                if (req.couponId && req.purities && req.purities.length) {
                    if (hasCommonFields) {
                        dispatchCalculation();
                    } else if (!req.purities.filter(({purityId}) => !purityId).length) {
                        if(req.amount) return
                        if (req.amount === '') {
                            dispatch(resetCalculations());
                        } else {
                            dispatchCalculation();
                        }
                    }
                }
            }

            localStorage.setItem(QUICK_CALCULATION_DATA, JSON.stringify(formState));
        }, 300);

        recalculate({
            dealForm: formState.dealForm,
            clientId: clientId,
            couponId: formState?.coupon?.value,
            type: type,
            amount: formState?.calculation?.amount,
            ...(formState?.calculation?.endDate ? { date: formState.calculation.endDate } : { firstPaymentDate: formState?.calculation?.firstPaymentDate }),
            ...(formState?.calculation?.months && { months: Number(formState?.calculation?.months?.value) }),
            ...(formState?.calculation?.firstPaymentDate && { firstPaymentDate: formState?.calculation?.firstPaymentDate }),
            purities: formState?.purities?.map((p) => ({
                grossWeight: p.grossWeight,
                pureWeight: p.pureWeight,
                purityId: p.purity.value,
                purityQuantity: p.quantity,
            })),
            estimatedValue: type === 'THINGS'
                ? isCalculationFromAmount ? watchEstimated : formState.things.estimatedValue
                : formState?.cars?.estimatedPrice,
            minDuration: formState.minDuration,
            maxDuration: formState.maxDuration,
            waiverOfRedemption: shouldRenderWaiverOfRedemption ? formState?.calculation?.waiverOfRedemption || false : false,
            isNonCash: formState?.calculation?.isNonCash
        });

        setRenderCalculationCouponForm(true)

        return () => {
            recalculate.cancel();
        };
    }, [formState, dispatch]);


    const updateCashBox = useCallback((cashBoxId) => {
        dispatch(getCashbox({id: cashBoxId, active: true, type: formState.typeContractTab}));
        dispatch(resetCalculations())
    }, [formState, dispatch])

    const updateFormState = useCallback((newData) => {
        if (newData?.things && newData.things.estimatedValue) {
            setFormState((prev) => _merge(_cloneDeep(prev), newData));
        } else if (newData?.cars) {
            setFormState((prev) => _merge(_cloneDeep(prev), newData));
        } else if (newData?.purities && !newData?.purities.find((i) => (Number(i.grossWeight) > 0 && Number(i.pureWeight) > 0) === false) && !newData?.purities.find((i) => i.purity.value === '')) {
            setFormState((prev) => ({
                ...prev, purities: _cloneDeep(newData.purities), additionalInformation: newData.additionalInformation
            }));
        } else if (
            newData?.calculation?.amount ||
            (newData?.calculation?.amount && newData?.calculation?.endDate) || 
            (newData?.calculation?.amount && newData?.calculation?.months) || 
            (newData?.calculation?.amount && newData?.calculation?.firstPaymentDate && newData?.calculation?.months) || 
            newData?.calculation?.waiverOfRedemption === true) {
            const todayDate = moment().format('YYYY-MM-DD')
            if (newData.calculation.waiverOfRedemption === true) newData.calculation.endDate = todayDate
            setFormState((prev) => _merge(_cloneDeep(prev), newData));
        } else if (newData?.cashbox || newData?.coupon) {
            setFormState((prev) => _merge(_cloneDeep(prev), newData));
        } else if (newData?.things && newData.things.estimatedValue === '') {
            setFormState((prev) => ({
                ...prev, things: {
                    ...newData.things,
                    imei: newData.things.imei.toUpperCase(),
                    sn: newData.things.sn.toUpperCase(),
                    additionalInfo: newData.things.additionalInfo
                }, additionalInformation: newData.additionalInformation, calculation: {},
            }));
            dispatch(resetCalculations());
        }
    }, [dispatch],);

    const onClickAvailableAmount = (value) => {
        if (formState.typeTab === 1) {

            const stateEstimatedValue = formState.things.estimatedValue === '' ? 0 : +formState.things.estimatedValue;

            const estimatedValue = stateEstimatedValue > 0 ? stateEstimatedValue - value : stateEstimatedValue + value;

            setFormState((prev) => ({
                ...prev, things: {
                    ...prev.things,
                    estimatedValue: accessFeatures['calculationAmountContractsThings'] ? value : estimatedValue.toFixed(2)
                },
            }));
        }
        if (formState.typeTab === 2) {

            const stateEstimatedPrice = formState.cars.estimatedPrice === '' ? 0 : +formState.cars.estimatedPrice;

            const estimatedPrice = formState.cars.estimatedPrice > 0 ? stateEstimatedPrice - value : stateEstimatedPrice + value;

            setFormState((prev) => ({
                ...prev, cars: {
                    ...prev.cars, estimatedPrice: estimatedPrice.toFixed(2),
                },
            }));
        }

    };

    const toggleJewelry = () => {
        dispatch(contractTypeJewellery())
        contextCalculationLayout.reset()
        setFormState(prevState => ({
            ...prevState, additionalInformation: ''
        }))

    }

    const toggleThings = () => {
        dispatch(contractTypeThings())
        contextCalculationLayout.reset()
        setFormState(prevState => ({
            ...prevState, additionalInformation: '', additionalInfo: ''
        }))
    }

    const toggleCars = () => {
        dispatch(contractTypeCars())
        contextCalculationLayout.reset()
    }

    useEffect(() => {
        if (isClear) {
            setFormState({
                ...formState,
                coupon: {},
                cashbox: {},
                maxDuration: null,
                minDuration: null,
                calculation: {},
                purities: _cloneDeep(defaultPurities),
                things: {...defaultThings},
                cars: _cloneDeep(defaultCars),
                additionalInformation: ''
            })
            dispatch(resetCalculations());
        }

    }, [isClear]);

    const getCurrentDealDuration = () => {
        switch (formState.dealForm) {
            case dealTypes.ANNUITY_HYBRID.dealForm:
            case dealTypes.ANNUITY_DELAYED_START.dealForm: {
                return currentDealDuration + 1;
            }
            case dealTypes.ANNUITY_CLASSIC.dealForm: {
                return currentDealDuration;
            }
            default: {
                return 1
            }
        }
    }

    const commonCalculationProps = {
        defaultFormValues: formState.calculation,
        calculationResult: {...calculations, currentDealDuration: getCurrentDealDuration()},
        amountError: commonError,
        typeTabValue: formState.typeTab,
        maxDuration: formState.maxDuration,
        minDuration: formState.minDuration,
        calculationFaild: calculationFaild,
        setContextCalculationLayout: setContextCalculationLayout,
        isClear: isClear,
        shouldRenderWaiverOfRedemption: shouldRenderWaiverOfRedemption,
        clientId: clientId,
        isDebtLoadData: isDebtLoadData,
        handleCloseDebtLoadModal: handleCloseDebtLoadModal,
        estimatedValue: watchEstimated,
        onUpdate: updateFormState,
        watchEstimate: setWatchEstimated,
        onClickAvailableAmount: onClickAvailableAmount,
        isCalculationFromAmount: setIsCalculationFromAmount,
        isEstimatedValue: setIsEstimatedValue,
        setWaiverOfRedemption: setWaiverOfRedemption,
      };

    return (<>
        {renderCalculationCouponForm && <CalculationCouponForm
            onUpdate={updateFormState}
            isClear={isClear}
            defaultCoupon={formState.coupon}
            defaultCashBox={formState.cashbox}
            onError={onError}
            cashBox={cashbox}
            cashboxes={cashboxes}
            updateCashBox={updateCashBox}
            waiverOfRedemption={waiverOfRedemption}
            calculationAmount={formState.calculation.amount}
            calculationEndDate={formState.calculation.endDate}
            calculateEndMonths={formState.calculation.months?.value}
            calculateFirstPaymentDate={formState.calculation.firstPaymentDate}
        />}

        {/* Драг металлы / Вещи */}
        <Tabs defaultIndex={formState.typeTab} onSelect={handleTypeTabSelect}>
            <TabList className="content-tabs offset">
                <Tab className="button-tab-pillbox" selectedClassName="active" onClick={toggleJewelry}>
                    Драгоценные металлы
                </Tab>
                <Tab className="button-tab-pillbox" selectedClassName="active" onClick={toggleThings}>
                    Вещи
                </Tab>
                {accessFeatures['cars.feature'] &&
                    <Tab className="button-tab-pillbox" selectedClassName="active" onClick={toggleCars}>
                        Автомобили
                    </Tab>}
            </TabList>
            <TabPanel>
                <PuritiesForm
                    defaultFormValues={formState}
                    onUpdate={updateFormState}
                    calculationResult={calculations}
                    isClear={isClear}
                    setContextForm={setContextForm}
                />
            </TabPanel>
            <TabPanel>
                <ThingsForm
                    defaultFormValues={formState}
                    onUpdate={updateFormState}
                    calculationResult={calculations}
                    isClear={isClear}
                    setContextForm={setContextForm}
                    isCalculationFromAmount={setIsCalculationFromAmount}
                    watchEstimate={setWatchEstimated}
                    isEstimatedValue={setIsEstimatedValue}
                />
            </TabPanel>
            <TabPanel>
                <CarsForm
                    defaultFormValues={formState.cars}
                    onUpdate={updateFormState}
                    calculationResult={calculations}
                    isClear={isClear}
                    setContextForm={setContextForm}
                />
            </TabPanel>
        </Tabs>

        <Tabs selectedIndex={activeDealTypeIndex} onSelect={handleDealTypeTabSelect}>
            <TabList className="offset contractForm-tabs">
                {isAnnuityFeature ? dealFormTabs.map(({dealForm, label}) => (
                    <Tab key={dealForm} className="button-tab-pillbox" selectedClassName="active">
                        {label}
                    </Tab>
                )) : 
                <fieldset class="p-b-0">
                    <div class="content-section-title">
                        <div class="content-section-title-actionitems nostretch">
                            <h2>Расчёт</h2>
                        </div>
                    </div>
                </fieldset>}
            </TabList>
        <TabPanel>
            <CalculationLayout {...commonCalculationProps} />
        </TabPanel>
        <TabPanel>
            <CalculationLayout isAnnuity {...commonCalculationProps} />
        </TabPanel>
        {!showAnnuityAuto && (
            <TabPanel>
                <CalculationLayout isHybrid {...commonCalculationProps} />
            </TabPanel>
        )}
        </Tabs>

    </>);
}

export default ContractForm;
